import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../config/api';
import { Layers, Plus, Search, Edit, Trash2, Save, Loader2, ArrowLeft, RotateCcw, X, Info, Hash } from 'lucide-react';
import toast from '../utils/toast';
import SearchBar from '../components/SearchBar';
import {
  masterAccentButton, masterHeaderCard, masterHeaderIcon, masterInput, masterInputLabel,
  masterPageShell, masterToolbar, masterPanelPadding, masterPrimaryButton,
  masterSearchInput, masterSearchWrap, masterSelect, masterSubtitle, masterTitle, masterTableData
} from '../components/masterPageStyles';

const emptyForm = {
  medicine_id: '', 
  typeId: '', 
  type: '', 
  categoryId: '', 
  category: '', 
  genericId: '', 
  generic_name: '', 
  medicine_name: '',
  unit_id: '',
  base_unit_name: '',
  purchase_unit_name: '',
  selectedSuppliers: [], 
  unit: '', 
 conversion_factor: [],
  companyId: '', 
  company_name: '', 
  hsn: '', 
  gst_per: '',
  reorder_level: '', 
  reorder_qty: '', 
  restricted_flag: 'No', 
  narcotics_flag: 'No', 
  mode: 'Expiry', 
  status: 'Active'
};

const MedicineMaster = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState(emptyForm);
  const [allUnits, setAllUnits] = useState([]);

  const [types, setTypes] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [allGenerics, setAllGenerics] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [med, t, cat, gen, comp, sup, unitRes] = await Promise.all([
        api.get('/master/medicine/all'),
        api.get('/master/type/all'),
        api.get('/master/medicine-category/all'),
        api.get('/master/generic/all'),
        api.get('/master/company/all'),
        api.get('/master/suppliers/all'),
        api.get('/master/unit-measurements/all')
      ]);
      setItems(med.data.data || []);
      setTypes(t.data.data || []);
      setAllCategories(cat.data.data || []);
      setAllGenerics(gen.data.data || []);
      setCompanies(comp.data.data || []);
      setSuppliers(sup.data.data || []);
      setAllUnits(unitRes.data.data || []);
    } catch (err) { toast.error("Sync Error"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Fetch Category-wise ID
  const fetchNextId = async (catName) => {
    if (!catName || editId) return; // Don't fetch if editing or no category
    try {
      const res = await api.get(`/master/medicine/latest-id?category=${encodeURIComponent(catName)}`);
      if (res.data?.success) setFormData(p => ({ ...p, medicine_id: res.data.nextId }));
    } catch (_) { }
  };



const filteredCategories = useMemo(() => {
    return allCategories.filter(c => 
      c.type === formData.type && 
      (c.status === 'Active' || c.category_name === formData.category)
    );
  }, [formData.type, allCategories, formData.category]);

  const filteredGenerics = useMemo(() => {
    return allGenerics.filter(g => 
      g.category === formData.category && 
      (g.status === 'Active' || g.genericName === formData.generic_name)
    );
  }, [formData.category, allGenerics, formData.generic_name]);
  
  const filteredData = useMemo(() => {
    const sortedList = [...items].sort((a, b) => {
      const idA = String(a.medicine_id || a._id || '');
      const idB = String(b.medicine_id || b._id || '');
      return idB.localeCompare(idA, undefined, { numeric: true, sensitivity: 'base' });
    });

    const q = searchTerm.trim().toLowerCase();
    if (!q) return sortedList;

    return sortedList.filter((item) => {
      const medName = (item.medicine_name || '').toLowerCase();
      const medId = (item.medicine_id || '').toLowerCase();
      const genericName = (item.generic_name || '').toLowerCase();

      return medName.startsWith(q) || medId.startsWith(q) || genericName.startsWith(q);
    });
  }, [items, searchTerm]);

  const paginatedData = useMemo(() => {
    if (pageSize === -1) return filteredData;
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

const filteredPurchaseUnits = useMemo(() => {
  if (!formData.base_unit_name) return [];
  
  const masterRecords = allUnits.filter(u => 
    u.base_unit_name === formData.base_unit_name && 
    (u.status === 'Active' || u.purchase_unit_name === formData.purchase_unit_name)
  );
  
  return masterRecords.map(rec => ({
    value: rec.purchase_unit_name,
    label: rec.purchase_unit_name
  }));
}, [formData.base_unit_name, allUnits, formData.purchase_unit_name]);

const addFactor = () => {
  setFormData(prev => {
    const current = Array.isArray(prev.conversion_factor) ? prev.conversion_factor : [];
    
    // Logic: If the list is empty, add the first blank spot.
    // If not empty, add a new blank spot at the end of the series.
    return {
      ...prev,
      conversion_factor: [...current, ""] 
    };
  });
};

const handleFactorChange = (index, value) => {
  const updated = [...formData.conversion_factor];
  // Logic: Keep it as a string while typing so it can be blank, 
  // we will convert to Number only during handleSubmit.
  updated[index] = value; 
  setFormData(prev => ({ ...prev, conversion_factor: updated }));
};

const handleChange = (e) => {
  const { name, value } = e.target;

  // 1. BASE UNIT LOGIC
  if (name === 'base_unit_name') {
    setFormData(prev => ({ 
      ...prev, 
      base_unit_name: value, 
      unit_id: '', // Reset ID until Purchase Unit is selected
      purchase_unit_name: '' // Clear purchase unit dropdown
    }));
    return;
  }

  // 2. PURCHASE UNIT LOGIC (Finds the exact UM-ID)
  if (name === 'purchase_unit_name') {
    // Find the specific record matching both selections
    const selectedUnitRecord = allUnits.find(u => 
      u.base_unit_name === formData.base_unit_name && 
      u.purchase_unit_name === value
    );

    setFormData(prev => ({ 
      ...prev, 
      purchase_unit_name: value, 
      unit_id: selectedUnitRecord?.unit_id || '' // Now we store the correct "UM-1" or "UM-2"
    }));
    return;
  }

  // 3. TYPE LOGIC
  if (name === 'type') {
    const selectedType = types.find((item) => item.typeName === value);
    setFormData(prev => ({ 
      ...prev, 
      type: value, 
      typeId: selectedType?.typeId || '', 
      category: '', 
      categoryId: '', 
      generic_name: '', 
      genericId: '', 
      medicine_id: '' 
    }));
    return;
  }

  // 4. CATEGORY LOGIC
  if (name === 'category') {
    const selectedCategory = allCategories.find((item) => item.category_name === value);
    setFormData(prev => ({ 
      ...prev, 
      category: value, 
      categoryId: selectedCategory?.category_id || '', 
      generic_name: '', 
      genericId: '',
      medicine_id: '' 
    }));
    return;
  }

  // 5. GENERIC LOGIC
  if (name === 'generic_name') {
    const selectedGeneric = allGenerics.find((item) => item.genericName === value);
    setFormData(prev => ({ 
      ...prev, 
      generic_name: value, 
      genericId: selectedGeneric?.genericId || '' 
    }));
    return;
  }

  // 6. COMPANY LOGIC
  if (name === 'company_name') {
    const selectedCompany = companies.find((item) => item.companyName === value);
    setFormData(prev => ({ 
      ...prev, 
      company_name: value, 
      companyId: selectedCompany?.companyId || '' 
    }));
    return;
  }

  // 7. DEFAULT CASE
  setFormData(prev => ({ ...prev, [name]: value }));
};

  const handleAddSupplier = (e) => {
    const val = e.target.value;
    if (!val) return;
    if (formData.selectedSuppliers.some(s => s.supplier_name === val)) {
      toast.error("Supplier already added");
      return;
    }
    const target = suppliers.find(s => s.supplier_name === val);
    if (target) {
      setFormData(prev => ({
        ...prev,
        selectedSuppliers: [...prev.selectedSuppliers, { supplierId: target.supplier_id, supplier_name: target.supplier_name }]
      }));
    }
    e.target.value = "";
  };

  const removeSupplier = (id) => {
    setFormData(prev => ({
      ...prev,
      selectedSuppliers: prev.selectedSuppliers.filter(s => s.supplierId !== id)
    }));
  };

  const handleEdit = (item) => {
    setEditId(item._id);
    setFormData({
      ...item,
     conversion_factor: Array.isArray(item.conversion_factor) 
      ? item.conversion_factor 
      : [item.conversion_factor || 1],
      unit_id: item.unit_id || '',
      base_unit_name: item.base_unit_name || '',
      purchase_unit_name: item.purchase_unit_name || '',
      selectedSuppliers: item.suppliers || (item.supplierId ? [{ supplierId: item.supplierId, supplier_name: item.supplier_name }] : []),
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.selectedSuppliers.length === 0) return toast.error("Please select at least one supplier");

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        id: editId,
        medicine_code: formData.medicine_id,
       conversion_factor: formData.conversion_factor
        .filter(f => f !== "" && f !== null)
        .map(Number),
        gst_per: Number(formData.gst_per) || 0,
        reorder_level: Number(formData.reorder_level) || 0,
        reorder_qty: Number(formData.reorder_qty) || 0
      };
      await api.post('/master/medicine/upsert', payload);
      toast.success(editId ? 'Updated' : 'Saved');

      // ─── CLEAR ALL FORM ARRAYS AND FIELDS ON SUCCESS ───
      setEditId(null);
      setFormData(emptyForm);

      setShowForm(true);
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.message || "Save Failed"); 
    } finally { 
      setIsSaving(false); 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete Medicine?")) return;
    await api.delete(`/master/medicine/${id}`);
    fetchData();
  };

  return (
    <div className={`${masterPageShell} p-1`}>
      <header className={`${masterHeaderCard} mb-1`}>
        <div className="flex items-center gap-1">
          <div className={masterHeaderIcon}><Layers size={10} /></div>
          <div><h1 className={masterTitle}>Medicine Master</h1><p className={masterSubtitle}>Catalog Setup</p></div>
        </div>
       {!showForm ? (
          <button onClick={() => { setEditId(null); setFormData(emptyForm); setShowForm(true); }} className={masterAccentButton}><Plus size={14} /> Add Medicine</button>
        ) : (
          <button 
            onClick={() => { 
              setEditId(null); 
              setFormData(emptyForm); // Clears text values when returning to the grid list
              setShowForm(false); 
            }} 
            className={masterPrimaryButton}
          >
            <ArrowLeft size={14} /> VIEW LIST
          </button>
        )}
      </header>

      <main className="mx-auto max-w-7xl">
        {showForm ? (
          <form onSubmit={handleSubmit} className={`${masterPanelPadding} space-y-1`}>
            <div className="flex items-center justify-between border-b">
              <h4 className="text-xs font-bold text-slate-800 uppercase">{editId ? 'Edit Medicine' : 'New Medicine'}</h4>
              
              <div className="flex items-center gap-1">
                {/* MEDICINE ID DISPLAYED NEXT TO RESET */}
                <div className="flex items-center gap-2 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                    {/* <Hash size={12} className="text-emerald-600"/> */}
                    <span className="text-xs font-bold text-emerald-700 tracking-wider">
                        {formData.medicine_id || 'AUTO-ID'}
                    </span>
                </div>

                <button type="button" onClick={() => setFormData(emptyForm)} className="text-[10px] font-bold text-rose-500 uppercase flex items-center gap-1 hover:underline">
                    <RotateCcw size={12}/> Reset
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-1 gap-y-1">
              <Select label="Type" name="type" value={formData.type} onChange={handleChange} options={types.filter(t => t.status === 'Active' || t.typeName === formData.type).map(t => ({value: t.typeName, label: t.typeName}))} required />
              <Select label="Category" name="category" value={formData.category} onChange={handleChange} options={filteredCategories.map(c => ({value: c.category_name, label: c.category_name}))} disabled={!formData.type} required />
              <Select label="Generic" name="generic_name" value={formData.generic_name} onChange={handleChange} options={filteredGenerics.map(g => ({value: g.genericName, label: g.genericName}))} disabled={!formData.category} required />
              <Input label="Medicine Name" name="medicine_name" value={formData.medicine_name} onChange={handleChange} required />
              
  
             <Select 
                  label="Base Unit" 
                  name="base_unit_name" 
                  value={formData.base_unit_name} 
                  onChange={handleChange} 
                  options={[...new Set(allUnits.filter(u => u.status === 'Active' || u.base_unit_name === formData.base_unit_name).map(u => u.base_unit_name))].map(name => ({
                    value: name, 
                    label: name
                  }))} 
                  required 
                />

              {/* PURCHASE UNIT - Filtered based on Base Unit Selection */}
              <Select 
                label="Purchase Unit" 
                name="purchase_unit_name" 
                value={formData.purchase_unit_name} 
                onChange={handleChange} 
                options={filteredPurchaseUnits} 
                disabled={!formData.base_unit_name} 
                required 
              />
              
        {/* CONVERSION FACTOR SECTION - FIXED TO SAME ROW */}
            <div className="col-span-2 border border-slate-200 rounded-md p-0.5 bg-slate-50/50">
              <div className="flex items-center justify-between">
                <label className={`${masterInputLabel} !mb-0`}>Conversion Factors</label>
                <button 
                  type="button" 
                  onClick={addFactor} 
                  className="text-black bg-white border border-black rounded hover:bg-emerald-100"
                >
                  <Plus size={12} />
                </button>
              </div>
              
              <div className="flex flex-row flex-wrap gap-1 items-center min-h-[20px]">
                {(formData.conversion_factor || []).map((factor, index) => (
                  <div key={index} className="flex items-center bg-white border border-slate-400 rounded shadow-sm h-5">
                    <span className="bg-slate-100 px-1 h-full flex items-center text-[9px] border-r border-slate-400 text-slate-500 font-bold">
                      {index + 1}
                    </span>
                    <input
                      type="number"
                      value={factor}
                      onChange={(e) => handleFactorChange(index, e.target.value)}
                      className="w-10 h-full text-center text-xs focus:outline-none"
                      placeholder="0"
                    />
                    <button 
                      type="button" 
                      onClick={() => {
                        const filtered = formData.conversion_factor.filter((_, i) => i !== index);
                        setFormData(prev => ({ ...prev, conversion_factor: filtered }));
                      }}
                      className="px-1 text-rose-500 hover:text-rose-700"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
                {formData.conversion_factor.length === 0 && (
                  <span className="text-[10px] text-slate-400">No factors added</span>
                )}
              </div>
            </div>
              <Select label="Company" name="company_name" value={formData.company_name} onChange={handleChange} options={companies.filter(c => c.status === 'Active' || c.companyName === formData.company_name).map(c => ({value: c.companyName, label: c.companyName}))} required />
              <Input label="HSN" name="hsn" value={formData.hsn} onChange={handleChange} />
              <Input label="GST (%)" name="gst_per" type="number" value={formData.gst_per} onChange={handleChange} />
              <Input label="MIN STOCK QTY" name="reorder_level" type="number" value={formData.reorder_level} onChange={handleChange} />
              <Input label="MIN ORDER QTY" name="reorder_qty" type="number" value={formData.reorder_qty} onChange={handleChange} />
              
              <Select label="Mode" name="mode" value={formData.mode} onChange={handleChange} options={['Expiry', 'Non-Expiry']} />
              <Select label="Status" name="status" value={formData.status} onChange={handleChange} options={['Active', 'Inactive']} />
              <Select label="Restricted" name="restricted_flag" value={formData.restricted_flag} onChange={handleChange} options={['Yes', 'No']} />
              <Select label="Narcotics" name="narcotics_flag" value={formData.narcotics_flag} onChange={handleChange} options={['Yes', 'No']} />
            </div>

            <hr className="my-2 border-slate-200" />
            
            {/* COMPACT SUPPLIER SECTION */}
            <div className="bg-slate-50 p-2 px-3 rounded-lg border border-slate-300">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[10px] font-bold text-slate-600 uppercase flex items-center gap-1">
                    <Plus size={12} className="text-emerald-600"/> Supplier Priority
                    </h3>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <div className="w-40">
                    <select className={`${masterSelect} !h-6 !py-0.5 !text-xs`} onChange={handleAddSupplier} defaultValue="">
                        <option value="">+ Add Supplier</option>
                        {suppliers.filter(s => s.status === 'Active').map(s => (
                              <option key={s.supplier_id} value={s.supplier_name}>{s.supplier_name}</option>
                            ))}
                    </select>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                    {formData.selectedSuppliers.map((sup, index) => (
                        <div key={sup.supplierId} className="flex items-center gap-1.5 bg-white border border-slate-300 pl-1 pr-1 py-0.5 rounded shadow-sm">
                        <span className="bg-emerald-600 text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                            {index + 1}
                        </span>
                        <span className="text-[11px] font-bold text-slate-700">{sup.supplier_name}</span>
                        <button type="button" onClick={() => removeSupplier(sup.supplierId)} className="text-slate-400 hover:text-rose-500 transition-colors">
                            <X size={12} />
                        </button>
                        </div>
                    ))}
                    </div>
                </div>
            </div>

            <div className="pt-4 border-t flex justify-end">
              <button disabled={isSaving} type="submit" className={masterAccentButton}>
                {isSaving ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>} Save Medicine
              </button>
            </div>
          </form>
        ) : (
          <div className="max-w-7xl mx-auto bg-white rounded-xl border border-slate-400 overflow-hidden shadow-sm">
            <div>
              <SearchBar 
                value={searchTerm} 
                onChange={(val) => {
                  setSearchTerm(val);
                  setCurrentPage(1);
                }} 
                placeholder="Search alphabetically by medicine name, ID, or generic name..." 
                totalItems={filteredData.length} 
                currentPage={currentPage} 
                pageSize={pageSize} 
                onPageChange={setCurrentPage} 
                onPageSizeChange={setPageSize} 
              />
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr>
                    <th className={masterTableData}>S.No</th>
                    <th className={masterTableData}>Generic Name</th>
                    <th className={masterTableData}>Medicine Name</th>
                    <th className={masterTableData}>Primary Supplier</th>
                    <th className={masterTableData}>Status</th>
                    <th className={masterTableData}>Actions</th>
                  </tr>
                </thead>
             <tbody className="divide-y divide-slate-300 font-bold text-slate-600">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <tr key={item._id} className="hover:bg-emerald-50/30">
                      <td className={masterTableData}>{index + 1 + (currentPage - 1) * pageSize}</td>
                      <td className="p-1 border border-slate-400 text-center text-emerald-600">{item.generic_name}</td>
                      <td className={masterTableData}>{item.medicine_name}</td>
                      <td className={masterTableData}>
                        <div className="flex flex-col">
                          <span>{item.suppliers?.[0]?.supplier_name || item.supplier_name || 'N/A'}</span>
                          {item.suppliers?.length > 1 && (
                            <span className="text-[9px] text-emerald-600">+{item.suppliers.length - 1} more</span>
                          )}
                        </div>
                      </td>
                      <td className={masterTableData}>
                        <span className={`px-2 py-0.5 rounded text-[9px] ${item.status === 'Active' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className={masterTableData}>
                        <div className="flex justify-center gap-1">
                          <button onClick={() => handleEdit(item)} className="p-1 text-emerald-600 hover:bg-emerald-100"><Edit size={14} /></button>
                          <button onClick={() => handleDelete(item._id)} className="p-1 text-rose-600 hover:bg-rose-100"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="p-12 text-center text-slate-400 text-xs italic">
                      {loading ? "Loading..." : "No medicines found"}
                    </td>
                  </tr>
                )}
              </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

const Input = ({ label, required, ...props }) => (
  <div className="flex flex-col"><label className={masterInputLabel}>{label}</label>
  <input required={required} {...props} className={masterInput} /></div>
);

const Select = ({ label, options, ...props }) => (
  <div className="flex flex-col "><label className={masterInputLabel}>{label}</label>
    <select {...props} className={masterSelect}>
      <option value="">-- SELECT --</option>
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const labelValue = typeof opt === 'string' ? opt : opt.label;
        return <option key={value} value={value}>{labelValue}</option>;
      })}
    </select>
  </div>
);

export default MedicineMaster;